import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[#6B7280] hover:text-[#0EA5E9] transition-colors mb-8" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-black text-[#111827] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="terms-title">
            Terms &amp; Conditions
          </h1>
          <p className="text-sm text-[#6B7280] mb-8">Last updated: February 2026</p>

          <div className="prose prose-slate max-w-none space-y-6 text-[#374151] text-sm leading-relaxed">

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">1. Introduction</h2>
              <p>Welcome to <strong>Aptivon</strong> ("Platform"), accessible at <a href="https://aptivon.in" className="text-[#0EA5E9]">aptivon.in</a>. By creating an account or using any feature of the Platform you agree to these Terms &amp; Conditions ("Terms"). If you do not agree, please do not use the Platform.</p>
              <p>Aptivon is an educational SaaS (Software-as-a-Service) platform for JEE &amp; NEET aspirants. We provide AI-generated mock tests, an AI doubt solver, skill-based quiz pools, an All-India rank predictor, and a Super-Points-based premium reward store. We are <strong>not</strong> a gambling, betting, real-money-gaming or fantasy-sports operator.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">2. Eligibility</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Age:</strong> You must be at least 15 years old to register. Users under 18 must obtain parental/guardian consent before any paid purchase or subscription.</li>
                <li><strong>Geography:</strong> The Platform is intended for residents of India. Pricing is in Indian Rupees (INR).</li>
                <li><strong>Email verification:</strong> A 6-digit OTP delivered to your registered email is required to activate your account.</li>
                <li><strong>Accurate info:</strong> You must provide accurate name, email and date of birth at signup. Falsifying age may result in account termination.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">3. Nature of Activities — Skill-Based Education</h2>
              <p>Every competition, quiz pool, daily/weekly mock test and leaderboard on Aptivon is a <strong>game of skill</strong> as recognised under Indian law. Outcomes depend on the user's academic knowledge, accuracy and speed — never on chance. Aptivon does not offer betting, wagering or staking on uncertain events.</p>
              <p>Quiz pools that involve a points entry fee distribute points-only prizes among top-ranking participants based on demonstrable performance.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">4. Aptivon Pro — Subscriptions</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Monthly:</strong> ₹299/month, billed via Razorpay recurring subscriptions.</li>
                <li><strong>Annual:</strong> ₹2,499/year (≈30% saving).</li>
                <li><strong>Pro benefits:</strong> daily AI mock tests, unlimited weekly full-length mocks, unlimited AI doubt solver, 500 monthly bonus points, College Predictor included, Pro-only monthly leaderboard, eligibility for Super Points (⭐).</li>
                <li><strong>Auto-renewal:</strong> Your subscription auto-renews until cancelled. You may cancel anytime from <em>Profile → Subscription</em>; access continues until the end of the paid period.</li>
                <li><strong>Refunds:</strong> Pro subscriptions are non-refundable for periods already activated. Pro-rated refunds may be granted in cases of duplicate billing — write to <a href="mailto:support@aptivon.in" className="text-[#0EA5E9]">support@aptivon.in</a> within 7 days.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">5. Points &amp; Pricing</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>You receive <strong>100 free starter points</strong> on email verification.</li>
                <li>Additional points can be purchased via Razorpay at <strong>₹1 = 2 points</strong>.</li>
                <li>Per-transaction limits: minimum <strong>₹10</strong>, maximum <strong>₹200</strong> (a student-friendly cap to prevent overspending).</li>
                <li>Points are used as entry fees in skill-based quiz pools and have <strong>no monetary or cash redemption value</strong>.</li>
                <li>All point purchases are inclusive of applicable GST. A tax invoice is generated automatically on payment.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">6. Super Points (⭐) &amp; Premium Reward Store</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Super Points (⭐) are awarded to the top 3% of Pro subscribers each calendar month based on the Pro monthly leaderboard.</li>
                <li>Tiered MAU-based issuance: when active Pro users exceed 1,000, ranks 1/2/3 receive 15,000 / 10,000 / 5,000 ⭐ respectively. Below this threshold, top-3% earn a flat 100 ⭐.</li>
                <li>Super Points can be redeemed only in the Premium Store at a fixed rate of <strong>1 ⭐ = ₹0.50</strong>, for Amazon and Flipkart gift cards (denominations from ₹100 to ₹7,500).</li>
                <li>Super Points cannot be converted to cash, transferred to another user, or combined with regular points.</li>
                <li>Aptivon may modify the Super-Points issuance rules with 30 days' notice; existing balances will not be retroactively reduced.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">7. Daily &amp; Weekly Mock Tests</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Daily 30-question / 45-minute mocks are <strong>Pro-only</strong>. Free users get one full-length mock per ISO calendar week.</li>
                <li>Question content is <strong>AI-generated by Anthropic Claude</strong> using the Emergent LLM service. While carefully validated, AI-generated content may occasionally contain errors. Aptivon does not warrant zero error rate; report mistakes to <a href="mailto:support@aptivon.in" className="text-[#0EA5E9]">support@aptivon.in</a> for review.</li>
                <li>Marking scheme: +4 for each correct answer, -1 for each incorrect answer, 0 for unattempted.</li>
                <li>The same daily mock is shown to every user on the same calendar day, in IST, to enable fair leaderboards.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">8. AI Doubt Solver</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>You may upload an image or type a doubt to receive an AI-generated step-by-step solution.</li>
                <li>Free users: 5 solutions per day. Pro users: unlimited.</li>
                <li>By submitting an image you confirm you have the right to use it; do not upload copyrighted material, personal data, or images of other people.</li>
                <li>Solutions are AI-generated and provided "as is" — verify against official answer keys for stakes-bearing decisions.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">9. Fair Play &amp; Anti-Cheating</h2>
              <p>To keep the Platform a level playing field:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>During proctored quizzes we run <strong>on-device webcam analysis</strong> (TensorFlow.js BlazeFace + COCO-SSD) to detect face presence, multiple faces, and banned objects (phones, laptops, books). All analysis is local; no video stream leaves your browser.</li>
                <li>Three (3) detection violations during a quiz triggers automatic submission.</li>
                <li>Tab-switching, copy/paste and dev-tools tampering are monitored. Suspicious sub-5-second submissions are flagged.</li>
                <li>Any cheating, automation, multi-account abuse, or attempt to manipulate leaderboards results in account termination and forfeiture of all points and ⭐.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">10. Affiliate Course Offers</h2>
              <p>Aptivon partners with Unacademy, Aakash and Motion to surface discounted course offerings. Clicks are tracked and Aptivon may earn a commission. The course purchase agreement is solely between you and the partner; Aptivon makes no warranty regarding course content, refunds, or delivery.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">11. Refund Policy</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Points purchases:</strong> Unused, recently purchased points may be refunded within 7 days. Points already spent in any pool, mock, or feature are non-refundable.</li>
                <li><strong>Pro subscription:</strong> Activated subscription periods are non-refundable. Cancel before next billing date to avoid future charges.</li>
                <li><strong>Mock Test Pass / College Predictor:</strong> One-time purchases are non-refundable once unlocked.</li>
                <li>Send refund requests to <a href="mailto:support@aptivon.in" className="text-[#0EA5E9]">support@aptivon.in</a> with your order ID and reason. Decisions are at Aptivon's discretion and final.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">12. Account Termination</h2>
              <p>Aptivon may suspend or terminate accounts that:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Violate these Terms;</li>
                <li>Engage in cheating, automation, or fraudulent behaviour;</li>
                <li>Provide false personal information;</li>
                <li>Are operated by users below the minimum age without guardian consent.</li>
              </ul>
              <p>You may delete your account at any time from <em>Profile → Settings</em>. Some data may be retained as required by tax / GST / DPDP regulations.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">13. Intellectual Property</h2>
              <p>All Platform content (UI, code, branding, AI prompts and curated datasets) is owned by Aptivon. AI-generated questions and solutions are licensed to you for personal study only — you may not republish, resell, or train competing AI models on this content.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">14. Limitation of Liability</h2>
              <p>The Platform is provided on an "as is" basis. We do not guarantee uninterrupted service, error-free AI content, or any specific exam outcome. Aptivon's aggregate liability for any claim is limited to the fees you have paid in the 3 months preceding the claim.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">15. Changes to These Terms</h2>
              <p>We may update these Terms periodically. Material changes will be notified by email and via in-app banner. Continued use after the effective date constitutes acceptance.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">16. Governing Law</h2>
              <p>These Terms are governed by the laws of India. Any dispute is subject to the exclusive jurisdiction of the competent courts of Delhi, India.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-[#111827] mb-2">17. Contact</h2>
              <p>Questions? Write to <strong><a href="mailto:support@aptivon.in" className="text-[#0EA5E9]">support@aptivon.in</a></strong>.</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
